import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  Hash,
  Layers,
  Link2,
  Loader2,
  Maximize2,
  MapPin,
  Minimize2,
  Package as PackageIcon,
  PauseCircle,
  RefreshCw,
  Table2,
  Upload,
  Zap,
} from "lucide-react";
import GlowCard from "../components/GlowCard";
import PackageDocViewer, { DocJump } from "./PackageDocViewer";
import { attachPackageDocument, getPackageDocument, readPackageDocument } from "./api";
import {
  DocumentState,
  Evidence,
  PACKAGE_TYPES,
  PackageDocument,
  TABLE_KINDS,
  WorkPackage,
  documentStateOf,
} from "./types";

interface PackageDocumentsProps {
  projectId: string;
  packages: WorkPackage[];
  /** Which reader the server is on, so a read taken by an older one can be offered again. */
  extractorVersion: number;
  /** The admin shell follows a project rather than working it: no reading, no attaching. */
  admin?: boolean;
  /** A package the server gave back changed — the register upstream holds the truth. */
  onPackageChanged: (pkg: WorkPackage) => void;
}

/** Which list of facts the right-hand card is showing. */
type View = "tables" | "references" | "drawings" | "holds" | "materials";

const STATE_LABELS: Record<DocumentState, string> = {
  none: "No document",
  unread: "Not read",
  read: "Read",
  stale: "Read by an older reader",
};

const dash = (value: string | number | undefined | null): string =>
  value === undefined || value === null || value === "" ? "—" : String(value);

/** A date the way the document wrote it, saying so when it could not be understood. */
const dateCell = (value: { text: string; iso?: string } | undefined): string =>
  value ? (value.iso ?? `${value.text} (unreadable date)`) : "—";

const whereOf = (e: Evidence): string => `${e.heading}, row ${e.row}`;

/**
 * The Documents stage of an AWP project: every package's document, read into facts.
 *
 * The engineering side's Document AI reads one drawing pack and hands its words to a model. This
 * reads a work package, which is a different kind of document: almost entirely tables, and the
 * things an AWP rule checks are relations BETWEEN those tables in DIFFERENT packages — a CWP that
 * says its EWP is at Rev B against an EWP whose own cover says Rev 00. So the job here is to get
 * the rows out whole, with the table and row each fact came from, and the comparing happens at the
 * Review Engine once every package has been read.
 *
 * WHAT WAS NOT RECOGNISED IS SHOWN TOO. A table the reader could not place is listed as "Not
 * recognised" rather than dropped, and the warnings say what a document did not give up. A stage
 * that only ever reports success is a stage that teaches a reviewer to assume success.
 */
const PackageDocuments = ({
  projectId,
  packages,
  extractorVersion,
  admin = false,
  onPackageChanged,
}: PackageDocumentsProps) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [doc, setDoc] = useState<PackageDocument | null>(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [docError, setDocError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The package being read right now — one at a time, including during Read all. */
  const [reading, setReading] = useState<string | null>(null);
  const [bulk, setBulk] = useState<{ done: number; total: number } | null>(null);
  const [attaching, setAttaching] = useState(false);
  const [view, setView] = useState<View>("tables");
  const [openTable, setOpenTable] = useState<string | null>(null);
  const [jump, setJump] = useState<DocJump>({ key: 0 });
  const [full, setFull] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  // CWPs, then EWPs, then PWPs, and by number within each — the order the register reads in.
  const rows = useMemo(
    () =>
      [...packages].sort(
        (a, b) =>
          PACKAGE_TYPES.indexOf(a.type) - PACKAGE_TYPES.indexOf(b.type) ||
          a.packageNo.localeCompare(b.packageNo),
      ),
    [packages],
  );

  const stateOf = useCallback(
    (pkg: WorkPackage) => documentStateOf(pkg, extractorVersion),
    [extractorVersion],
  );

  const counts = useMemo(() => {
    const out = { none: 0, unread: 0, read: 0, stale: 0 };
    for (const pkg of rows) out[stateOf(pkg)] += 1;
    return out;
  }, [rows, stateOf]);

  const selected = useMemo(
    () => rows.find((p) => p.id === selectedId) ?? null,
    [rows, selectedId],
  );

  // Open something as soon as there is something to open: the first package still to be read, or
  // the first package. Landing on an empty pane makes the stage look broken when it is only idle.
  useEffect(() => {
    if (rows.length === 0 || (selectedId && rows.some((p) => p.id === selectedId))) return;
    const next = rows.find((p) => stateOf(p) === "unread") ?? rows[0];
    setSelectedId(next ? next.id : null);
  }, [rows, selectedId, stateOf]);

  /** What was read, fetched when a package that has been read is opened. */
  useEffect(() => {
    if (!selected || !selected.read) {
      setDoc(null);
      setDocError(null);
      return;
    }
    let live = true;
    setLoadingDoc(true);
    setDocError(null);
    void (async () => {
      try {
        const data = await getPackageDocument(projectId, selected.id);
        if (live) setDoc(data);
      } catch (err) {
        if (live) {
          setDoc(null);
          setDocError(err instanceof Error ? err.message : "Could not load what was read.");
        }
      } finally {
        if (live) setLoadingDoc(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [projectId, selected?.id, selected?.read?.at]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setOpenTable(null);
    setView("tables");
  }, [selected?.id]);

  // Esc leaves full screen, because that is what Esc does. The button stays the discoverable way.
  useEffect(() => {
    if (!full) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setFull(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [full]);

  /** Read one package's document. Returns what it read so Read all can keep count. */
  const readOne = useCallback(
    async (pkg: WorkPackage): Promise<boolean> => {
      setReading(pkg.id);
      try {
        const { package: updated, document } = await readPackageDocument(projectId, pkg.id);
        onPackageChanged(updated);
        // Shown straight away rather than fetched again: the read already returned everything, and
        // the package being read is always the one on screen — Read all selects each in turn, so a
        // reviewer watching it sees each document's facts appear as it goes.
        setDoc(document);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : `Could not read ${pkg.packageNo}.`);
        return false;
      } finally {
        setReading(null);
      }
    },
    [projectId, onPackageChanged],
  );

  /**
   * Read every package that still needs it, one after another.
   *
   * Sequential on purpose. Reading a Word package takes tens of milliseconds, so there is nothing
   * to gain from running them together, and a register being read one package at a time is a thing
   * a person can watch and stop understanding of. One failure does not end the run — the rest are
   * still read, and the failure is named.
   */
  const readAll = async () => {
    const todo = rows.filter((p) => p.source && stateOf(p) !== "read");
    if (todo.length === 0 || bulk) return;
    setError(null);
    setBulk({ done: 0, total: todo.length });
    for (let i = 0; i < todo.length; i += 1) {
      setSelectedId(todo[i]!.id);
      await readOne(todo[i]!);
      setBulk({ done: i + 1, total: todo.length });
    }
    setBulk(null);
  };

  const attach = async (file: File | undefined) => {
    if (!file || !selected || attaching) return;
    setAttaching(true);
    setError(null);
    try {
      const updated = await attachPackageDocument(projectId, selected.id, file);
      onPackageChanged(updated);
      setDoc(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not attach the document.");
    } finally {
      setAttaching(false);
    }
  };

  /** Send the document pane to where a fact was read from. */
  const goTo = (evidence: Evidence, quote?: string) =>
    setJump((prev) => ({ heading: evidence.heading, quote, key: prev.key + 1 }));

  if (rows.length === 0) {
    return (
      <GlowCard hover={false}>
        <div className="vr-soon">
          <div className="vr-soon-icon">
            <FileText size={28} />
          </div>
          <h2 className="vr-h-sec">No packages yet</h2>
          <p className="vr-muted" style={{ maxWidth: 460 }}>
            This project has no packages. Add them on the Packages step first; each one arrives as
            its document, and this is where that document is read.
          </p>
        </div>
      </GlowCard>
    );
  }

  const state = selected ? stateOf(selected) : "none";
  const busy = reading === selected?.id;
  const facts = doc?.facts;

  /** The counts the chips carry — zero is still worth showing, because zero is a result. */
  const chips: { key: View; label: string; n: number }[] = [
    { key: "tables", label: "Tables", n: doc?.tables.length ?? 0 },
    { key: "references", label: "References", n: facts?.references.length ?? 0 },
    { key: "drawings", label: "Drawings", n: facts?.drawings.length ?? 0 },
    { key: "holds", label: "Holds", n: facts?.holds.length ?? 0 },
    { key: "materials", label: "Materials", n: facts?.materials.length ?? 0 },
  ];

  const coverRows: [string, string][] = [
    ["Package number", dash(facts?.cover.packageNo)],
    ["Revision", dash(facts?.cover.revision)],
    ["Work area", dash(facts?.cover.cwa)],
    ["Discipline", dash(facts?.cover.discipline)],
    ["Status", dash(facts?.cover.status)],
    ["Issue date", facts?.cover.issueDate ? dateCell(facts.cover.issueDate) : "—"],
    ["Project code", dash(facts?.cover.projectCode)],
  ];

  return (
    <div className={`vr-docai-shell${full ? " vr-docai-fullscreen" : ""}`}>
      {/* The stage in one line: how far the register has got, and the one button that moves it. */}
      {!full && (
        <div className={`vr-awp-readout${counts.none > 0 ? " vr-awp-readout-warn" : ""}`}>
          <span>
            {counts.read} of {rows.length} {rows.length === 1 ? "package" : "packages"} read
            {counts.unread > 0 && ` · ${counts.unread} to read`}
            {counts.stale > 0 && ` · ${counts.stale} read by an older reader`}
            {counts.none > 0 &&
              ` · ${counts.none} ${counts.none === 1 ? "package has" : "packages have"} no document — open ${counts.none === 1 ? "it" : "each"} below and attach one`}
          </span>
          {!admin && (
            <button
              className="vr-btn vr-btn-sm vr-btn-outline"
              onClick={() => void readAll()}
              disabled={!!bulk || !!reading || rows.every((p) => !p.source || stateOf(p) === "read")}
            >
              {bulk ? (
                <>
                  <Loader2 size={13} className="vr-spin-inline" /> Reading {bulk.done + 1} of {bulk.total}…
                </>
              ) : (
                <>
                  <Zap size={13} /> Read all
                </>
              )}
            </button>
          )}
        </div>
      )}

      {error && <div className="vr-form-error">{error}</div>}

      <div className="vr-docai">
        <GlowCard className="vr-docai-source" hover={false}>
          <div className="vr-card-head">
            <Layers size={14} />
            <h3>Package Document</h3>
            <button
              className="vr-btn vr-btn-sm vr-btn-outline"
              onClick={() => setFull((open) => !open)}
              disabled={!full && !selected?.source}
              style={{ marginLeft: "auto" }}
              title="Full Screen Mode"
            >
              {full ? (
                <>
                  <Minimize2 size={13} /> Exit Full Screen
                </>
              ) : (
                <>
                  <Maximize2 size={13} /> Full Screen
                </>
              )}
            </button>
            {!admin && (
              <button
                className="vr-btn vr-btn-sm vr-btn-glow"
                onClick={() => selected && void readOne(selected)}
                disabled={!selected?.source || busy || !!bulk}
                title="Read this document into facts"
              >
                {busy ? (
                  <>
                    <Loader2 size={13} className="vr-spin-inline" /> Reading…
                  </>
                ) : (
                  <>
                    <Zap size={13} /> {state === "read" || state === "stale" ? "Read again" : "Read"}
                  </>
                )}
              </button>
            )}
          </div>

          {/* The picker is the register: every package, with where it has got to. */}
          <div className="vr-doc-picker">
            {rows.map((pkg) => {
              const s = stateOf(pkg);
              return (
                <button
                  key={pkg.id}
                  type="button"
                  className={`vr-doc-pick vr-pkg-pick vr-pkg-${s}${pkg.id === selectedId ? " vr-active" : ""}`}
                  onClick={() => setSelectedId(pkg.id)}
                  title={`${pkg.type} ${pkg.packageNo} — ${STATE_LABELS[s]}`}
                >
                  {reading === pkg.id ? (
                    <Loader2 size={12} className="vr-spin-inline" />
                  ) : s === "read" ? (
                    <CheckCircle2 size={12} />
                  ) : s === "none" ? (
                    <AlertTriangle size={12} />
                  ) : s === "stale" ? (
                    <RefreshCw size={12} />
                  ) : (
                    <PauseCircle size={12} />
                  )}
                  <span>{pkg.packageNo}</span>
                </button>
              );
            })}
          </div>

          {selected?.source ? (
            <div className="vr-doc-preview">
              <PackageDocViewer projectId={projectId} pkg={selected} jump={jump} />
            </div>
          ) : (
            // A package typed in by hand has no document, so nothing can be read from it and
            // nothing can be checked against it. This is where it gets one.
            <div
              className={`vr-drop${attaching ? " vr-drop-busy" : ""}`}
              onClick={() => !attaching && !admin && fileInput.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (!admin) void attach(e.dataTransfer.files?.[0]);
              }}
            >
              {attaching ? (
                <>
                  <Loader2 size={26} className="vr-spin-inline vr-drop-icon" />
                  <span className="vr-drop-title">Attaching the document…</span>
                </>
              ) : (
                <>
                  <Upload size={26} className="vr-drop-icon" />
                  <span className="vr-drop-title">
                    {admin
                      ? `${selected?.packageNo ?? "This package"} has no document`
                      : `Attach the document for ${selected?.packageNo ?? "this package"}`}
                  </span>
                  <span className="vr-drop-sub">
                    {admin
                      ? "Nothing can be read from it, and the review has nothing to check."
                      : "PDF, Word or Excel. Until it has one, nothing can be read from this package."}
                  </span>
                </>
              )}
              <input
                ref={fileInput}
                type="file"
                accept=".pdf,.docx,.xlsx,.xlsm,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                style={{ display: "none" }}
                onChange={(e) => {
                  void attach(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </div>
          )}
        </GlowCard>

        <div className="vr-docai-read">
          <GlowCard className="vr-table-card" hover={false}>
            <div className="vr-table-head">
              <div className="vr-card-head">
                <PackageIcon size={14} />
                <h3>Cover</h3>
              </div>
              {selected?.read && (
                <span className="vr-cell-code">
                  read {new Date(selected.read.at).toLocaleString()}
                </span>
              )}
            </div>
            <div className="vr-section-list">
              {!doc && (
                <div className="vr-empty">
                  {loadingDoc ? (
                    <>
                      <Loader2 size={14} className="vr-spin-inline" /> Loading what was read…
                    </>
                  ) : docError ? (
                    docError
                  ) : state === "none" ? (
                    "This package has no document to read."
                  ) : (
                    "Not read yet. Press Read to pull the tables out of this document."
                  )}
                </div>
              )}
              {doc &&
                coverRows.map(([label, value]) => (
                  <div key={label} className="vr-section-row">
                    <span className="vr-section-name">{label}</span>
                    <span className="vr-cell-code">{value}</span>
                  </div>
                ))}
            </div>
          </GlowCard>

          <GlowCard className="vr-table-card" hover={false}>
            <div className="vr-table-head">
              <div className="vr-card-head">
                <Table2 size={14} />
                <h3>What was read</h3>
              </div>
              <div className="vr-chips">
                {chips.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    className={`vr-chip-btn${view === c.key ? " vr-active" : ""}`}
                    onClick={() => setView(c.key)}
                    disabled={!doc}
                  >
                    {c.label} ({c.n})
                  </button>
                ))}
              </div>
            </div>

            <div className="vr-entity-list">
              {!doc && <div className="vr-empty">Nothing read from this package yet.</div>}

              {/* Warnings sit at the top of the list rather than in a banner: they belong to the
                  document in hand, and a banner above the split would be read as the stage's. */}
              {doc?.warnings.map((warning) => (
                <div key={warning} className="vr-pkg-warning">
                  <AlertTriangle size={13} />
                  <span>{warning}</span>
                </div>
              ))}

              {doc && view === "tables" &&
                doc.tables.map((table) => {
                  const kind = TABLE_KINDS[table.kind];
                  const open = openTable === table.id;
                  return (
                    <div key={table.id} className="vr-pkg-table">
                      <button
                        type="button"
                        className="vr-entity-row vr-entity-jump"
                        onClick={() => {
                          setOpenTable(open ? null : table.id);
                          goTo({ tableId: table.id, heading: table.heading, row: 1 });
                        }}
                        title={open ? "Hide the rows" : "Show the rows, and find this in the document"}
                      >
                        <MapPin size={12} />
                        <div className="vr-entity-body">
                          <span className="vr-entity-value">{table.heading}</span>
                          <span className="vr-entity-source">
                            {kind.label} — {table.rows.length} {table.rows.length === 1 ? "row" : "rows"}
                            {!kind.used && " — not used by the review"}
                          </span>
                        </div>
                        <span className={`vr-entity-conf${kind.used ? "" : " vr-low"}`}>
                          {table.columns.length || "?"} cols
                        </span>
                      </button>
                      {open && (
                        <div className="vr-pkg-rows">
                          <table>
                            {table.columns.length > 0 && (
                              <thead>
                                <tr>
                                  {table.columns.map((c, i) => (
                                    <th key={`${table.id}-c${i}`}>{c}</th>
                                  ))}
                                </tr>
                              </thead>
                            )}
                            <tbody>
                              {table.rows.map((row, r) => (
                                <tr key={`${table.id}-r${r}`}>
                                  {row.map((value, c) => (
                                    <td key={`${table.id}-r${r}-c${c}`}>{value}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}

              {doc && view === "references" &&
                facts!.references.map((ref, i) => (
                  <button
                    key={`${ref.packageNo}-${i}`}
                    type="button"
                    className="vr-entity-row vr-entity-jump"
                    onClick={() => goTo(ref.evidence, ref.packageNo)}
                  >
                    <Link2 size={12} />
                    <div className="vr-entity-body">
                      <span className="vr-entity-value">{ref.packageNo}</span>
                      <span className="vr-entity-source">
                        {[ref.type, ref.revision ? `Rev ${ref.revision}` : "", ref.role]
                          .filter(Boolean)
                          .join(" · ")}
                        {" — "}
                        {whereOf(ref.evidence)}
                      </span>
                    </div>
                  </button>
                ))}

              {doc && view === "drawings" &&
                facts!.drawings.map((d, i) => (
                  <button
                    key={`${d.number}-${i}`}
                    type="button"
                    className="vr-entity-row vr-entity-jump"
                    onClick={() => goTo(d.evidence, d.number)}
                  >
                    <Hash size={12} />
                    <div className="vr-entity-body">
                      <span className="vr-entity-value">{d.number}</span>
                      <span className="vr-entity-source">
                        {d.title ?? "No title"} — {whereOf(d.evidence)}
                      </span>
                    </div>
                    <span className="vr-entity-conf">Rev {dash(d.revision)}</span>
                  </button>
                ))}

              {doc && view === "holds" &&
                facts!.holds.map((h, i) => (
                  <button
                    key={`${h.holdId ?? "hold"}-${i}`}
                    type="button"
                    className="vr-entity-row vr-entity-jump"
                    onClick={() => goTo(h.evidence, h.holdId ?? h.description)}
                  >
                    <PauseCircle size={12} />
                    <div className="vr-entity-body">
                      <span className="vr-entity-value">{h.holdId ?? h.description}</span>
                      <span className="vr-entity-source">
                        {h.holdId ? `${h.description} — ` : ""}
                        {h.targetClose ? `closes ${dateCell(h.targetClose)}` : "no closing date"}
                        {h.status ? ` · ${h.status}` : ""} — {whereOf(h.evidence)}
                      </span>
                    </div>
                  </button>
                ))}

              {doc && view === "materials" &&
                facts!.materials.map((m, i) => (
                  <button
                    key={`${m.description}-${i}`}
                    type="button"
                    className="vr-entity-row vr-entity-jump"
                    onClick={() => goTo(m.evidence, m.description)}
                  >
                    <PackageIcon size={12} />
                    <div className="vr-entity-body">
                      <span className="vr-entity-value">{m.description}</span>
                      <span className="vr-entity-source">
                        {[
                          m.tag,
                          m.quantityText ? `${m.quantityText}${m.unit ? ` ${m.unit}` : ""}` : "",
                          m.pwpNo,
                          m.poDelivery ? `delivery ${dateCell(m.poDelivery)}` : "",
                          m.requiredAtSite ? `needed ${dateCell(m.requiredAtSite)}` : "",
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                        {" — "}
                        {whereOf(m.evidence)}
                      </span>
                    </div>
                  </button>
                ))}
            </div>
          </GlowCard>
        </div>
      </div>
    </div>
  );
};

export default PackageDocuments;
